import styled from '@emotion/styled';
import gsap from 'gsap';
import Link from 'next/link';
import { useRouter } from 'next/router';
import { useEffect, useRef, useState } from 'react';

import { APP_ROUTES, OUR_KEY_SERVICE } from 'constant/appConstant';
import { breakPoints } from 'constant/breakPoints';
import { EclipseLinearBlur } from 'styles/styled';

export default function OurKeyService() {
  const refWrap = useRef(null);
  const router = useRouter();
  const refContainer = useRef<HTMLDivElement>(null);
  const refTop = useRef<HTMLDivElement>(null);

  const [isShow, setIsShow] = useState(false);

  // Handle show top
  useEffect(() => {
    const observer = new IntersectionObserver(
      (entries) => {
        entries.forEach((entry) => {
          if (entry.isIntersecting) {
            setIsShow(true);
          }
        });
      },
      {
        threshold: 0.2,
      },
    );

    refWrap.current && observer.observe(refWrap.current);

    return () => {
      observer.disconnect();
    };
  }, []);

  // Handle container
  useEffect(() => {
    if (!refContainer.current || !refTop.current || !process.browser) return;

    const child = refContainer.current.children;
    const observers = [];

    for (let i = 0; i < child.length; i++) {
      const ele: any = child[i];

      observers[i] = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && refTop.current) {
              ele.classList.add('show');
              gsap.to(refTop.current, {
                translateY: ele.offsetTop - 70,
                duration: 0.2,
                ease: 'ease',
              });
            }
          });
        },
        {
          threshold: window.innerWidth < 1024 ? 0.2 : 0.8,
        },
      );

      observers[i].observe(ele);
    }
  }, []);

  // Handle top title
  useEffect(() => {
    if (!refContainer.current || !refTop.current) return;

    const child = refContainer.current.children;
    const observers = [];

    for (let i = 0; i < child.length; i++) {
      const ele: any = child[i];

      observers[i] = new IntersectionObserver(
        (entries) => {
          entries.forEach((entry) => {
            if (entry.isIntersecting && refTop.current) {
              gsap.to(refTop.current, {
                translateY: ele.offsetTop - 70,
                duration: 0.8,
                ease: 'ease',
              });
            }
          });
        },
        {
          threshold: 0.9,
        },
      );

      observers[i].observe(ele);
    }
  }, []);

  useEffect(() => {
    if (!process.browser) return;

    setTimeout(() => {
      const _id = router.asPath.slice(
        router.asPath.indexOf('#'),
        router.asPath.length,
      );

      const ele = document.querySelector(_id);

      if (!ele) return;

      ele.scrollIntoView({ behavior: 'smooth' });
    }, 500);
  }, [router]);

  return (
    <WrapBgr>
      <Bgr>
        <img src="/images/blur.webp" alt="Spirit labs services" />
        <img src="/images/blur.webp" alt="Spirit labs services" />
        <EclipseLinearBlur
          width="250px"
          top="20vw"
          right="-7vw"
          rotate={174.72}
        />
        <EclipseLinearBlur width="250px" top="85vw" left="-10vw" />
      </Bgr>

      <Wrap ref={refWrap} className={isShow ? 'show' : ''}>
        <Top ref={refTop}>
          <div className="title-wrap">
            Our Key <span>Service</span>
          </div>
          <Link href={APP_ROUTES.CONTACT_US} prefetch={false}>
            <a className="button button-link">Contact us {'>'}</a>
          </Link>
        </Top>
        <Container ref={refContainer}>
          {OUR_KEY_SERVICE.map((item, idx) => (
            <ServiceContainer key={idx}>
              <MarkId id={item.id} />
              <img src="/images/blur.webp" alt="Spirit labs services" />
              <ServiceTitle dangerouslySetInnerHTML={{ __html: item.title }} />
              {item.keys.map((service, serIdx) => (
                <ServiceItem key={serIdx}>
                  <ServiceIcon>
                    <img src={service.icon} alt="Spirit labs services" />
                  </ServiceIcon>
                  <ServiceItemTitle
                    dangerouslySetInnerHTML={{ __html: service.title }}
                  />
                  <ServiceItemDes
                    dangerouslySetInnerHTML={{ __html: service.des }}
                  />
                </ServiceItem>
              ))}
            </ServiceContainer>
          ))}
        </Container>
      </Wrap>
    </WrapBgr>
  );
}

const Top = styled.div`
  display: flex;
  align-items: center;
  justify-content: space-between;

  & > * {
    transition: width 1s 0.5s ease, height 1s 0.5s ease, font-size 0.5s ease,
      all 0.8s ease;
  }

  .button-link {
    padding: 10px 18px;
    line-height: 1;
  }

  @media screen and (max-width: ${breakPoints.desktopS - 1}px) {
    transform: none !important;
    .button-link {
      display: none;
    }
  }

  @media screen and (min-width: ${breakPoints.desktopS}px) {
    display: initial;
    min-width: 220px;
    width: 100%;

    .title-wrap {
      font-weight: 500;
      font-size: 96px;
      text-align: center;
    }

    .button-link {
      display: none;
      opacity: 0;
    }
  }
`;

const Container = styled.div`
  @media screen and (min-width: ${breakPoints.desktopS}px) {
    width: 100%;
    flex: 1;
  }
`;

const MarkId = styled.div`
  height: 0;
  position: absolute;
  left: 0;
  top: -200px;

  @media screen and (min-width: ${breakPoints.desktopS}px) {
    top: -350px;
  }
`;

const ServiceTitle = styled.div`
  color: var(--yellow);
  font-size: 18px;
  font-weight: 600;
  margin-bottom: 40px;

  @media screen and (min-width: ${breakPoints.desktopS}px) {
    font-size: 36px;
    width: 100%;
  }
`;

const ServiceItem = styled.div`
  margin-bottom: 36px;

  @media screen and (min-width: ${breakPoints.desktopS}px) {
    max-width: 288px;
    padding-right: 48px;
    flex: 1;
  }
`;

const ServiceIcon = styled.div`
  width: 60px;
  height: 60px;
  margin-bottom: 16px;

  img {
    object-fit: cover;
    height: 100%;
    width: auto;
  }

  @media screen and (max-width: ${breakPoints.tabletS}px) {
    width: 48px;
    height: 48px;
  }
`;

const ServiceItemTitle = styled.div`
  font-size: 16px;
  font-weight: 600;
  margin-bottom: 16px;

  @media screen and (min-width: ${breakPoints.desktopS}px) {
    font-size: 20px;
  }
`;

const ServiceItemDes = styled.div`
  color: var(--gray2);
`;

const ServiceContainer = styled.div`
  padding: 40px 0 8px;
  position: relative;

  & > *:not(img) {
    visibility: hidden;
    opacity: 0;
    transition: all 1s ease;
    transform: translateY(80px);
  }

  & > img {
    position: absolute;
    top: 0;
    left: 0;
  }

  &:nth-of-type(2n + 1) {
    & > img {
      transform: translate(-50%, -20%);
    }
  }

  &:nth-of-type(2n) {
    & > img {
      transform: translate(-10%, -20%);
    }
  }

  &.show {
    & > *:not(img) {
      visibility: visible;
      opacity: 1;
      transform: translateY(0);
    }

    ${MarkId} {
      top: -150px;
    }
  }

  @media screen and (min-width: ${breakPoints.desktopS}px) {
    display: flex;
    flex-wrap: wrap;
    padding-top: 0;

    & > img {
      display: none;
    }

    &:nth-of-type(1),
    &:nth-of-type(5) {
      ${ServiceItemTitle} {
        height: 94px;
      }
    }
  }
`;

const WrapBgr = styled.div`
  position: relative;

  @media screen and (max-width: ${breakPoints.tabletM}px) {
    z-index: 10;
  }
`;

const Bgr = styled.div`
  position: absolute;
  top: 0;
  left: 0;
  width: 100%;
  height: max-content;

  img {
    z-index: -1;
  }

  img:nth-of-type(1) {
    width: 900px;
    height: auto;
    transform: translate(-10%, -20%) scale(2);
  }

  img:nth-of-type(2) {
    width: 1123px;
    height: auto;
    margin-right: 0;
    margin-left: auto;
    transform: translate(40%, 30%) scale(2);
  }

  @media screen and (max-width: ${breakPoints.desktopS - 1}px) {
    img {
      display: none;
    }

    div:nth-of-type(1) {
      width: 143px;
      height: 143px;
      right: -73px;
      top: 800px;
    }

    div:nth-of-type(2) {
      width: 143px;
      height: 143px;
      right: -73px;
      left: initial;
      top: 2900px;
      transform: rotate(35deg);
    }
  }
`;

const Wrap = styled.div`
  padding: 72px 16px;
  position: relative;

  @media screen and (min-width: ${breakPoints.desktopS}px) {
    display: flex;
    max-width: 1140px;
    margin: 0 auto;
    flex-wrap: wrap;

    &.show {
      ${Top} {
        width: max-content;
        padding-right: 100px;

        .title-wrap {
          font-weight: 500;
          font-size: 48px;
          line-height: 130%;
          text-align: left;
          max-width: 220px;
          margin-bottom: 32px;

          span {
            display: block;
            margin-top: 16px;
          }
        }

        .button-link {
          opacity: 1;
          display: initial;
        }
      }
    }
  }
`;
